import { PoseData, PoseLandmark } from '../../types';

/**
 * PoseSmoother applies exponential moving average (EMA) smoothing to pose landmarks
 * to reduce jitter while maintaining responsiveness.
 * Validates Requirements 3.1.6, 3.1.7
 */
export class PoseSmoother {
  private previousSmoothed: PoseData['landmarks'] | null = null;

  /**
   * Apply smoothing to pose data using confidence-weighted EMA
   * @param currentPose Current raw pose data
   * @returns Smoothed pose data
   */
  smooth(currentPose: PoseData): PoseData {
    if (!currentPose.detected) {
      // No pose detected - reset smoother and return as-is
      this.previousSmoothed = null;
      return currentPose;
    }

    if (!this.previousSmoothed) {
      // First frame - store current as previous and return as-is
      this.previousSmoothed = this.cloneLandmarks(currentPose.landmarks);
      return currentPose;
    }

    // Apply smoothing to each landmark
    const smoothedLandmarks: PoseData['landmarks'] = {
      nose: this.smoothLandmark(currentPose.landmarks.nose, this.previousSmoothed.nose),
      leftEye: this.smoothLandmark(currentPose.landmarks.leftEye, this.previousSmoothed.leftEye),
      rightEye: this.smoothLandmark(currentPose.landmarks.rightEye, this.previousSmoothed.rightEye),
      leftEar: this.smoothLandmark(currentPose.landmarks.leftEar, this.previousSmoothed.leftEar),
      rightEar: this.smoothLandmark(currentPose.landmarks.rightEar, this.previousSmoothed.rightEar),
      leftShoulder: this.smoothLandmark(currentPose.landmarks.leftShoulder, this.previousSmoothed.leftShoulder),
      rightShoulder: this.smoothLandmark(currentPose.landmarks.rightShoulder, this.previousSmoothed.rightShoulder),
      leftElbow: this.smoothLandmark(currentPose.landmarks.leftElbow, this.previousSmoothed.leftElbow),
      rightElbow: this.smoothLandmark(currentPose.landmarks.rightElbow, this.previousSmoothed.rightElbow),
      leftWrist: this.smoothLandmark(currentPose.landmarks.leftWrist, this.previousSmoothed.leftWrist),
      rightWrist: this.smoothLandmark(currentPose.landmarks.rightWrist, this.previousSmoothed.rightWrist),
      leftHip: this.smoothLandmark(currentPose.landmarks.leftHip, this.previousSmoothed.leftHip),
      rightHip: this.smoothLandmark(currentPose.landmarks.rightHip, this.previousSmoothed.rightHip),
      leftKnee: this.smoothLandmark(currentPose.landmarks.leftKnee, this.previousSmoothed.leftKnee),
      rightKnee: this.smoothLandmark(currentPose.landmarks.rightKnee, this.previousSmoothed.rightKnee),
      leftAnkle: this.smoothLandmark(currentPose.landmarks.leftAnkle, this.previousSmoothed.leftAnkle),
      rightAnkle: this.smoothLandmark(currentPose.landmarks.rightAnkle, this.previousSmoothed.rightAnkle),
    };

    // Store smoothed landmarks for next frame
    this.previousSmoothed = smoothedLandmarks;

    return {
      ...currentPose,
      landmarks: smoothedLandmarks
    };
  }

  /**
   * Apply EMA smoothing to a single landmark with confidence-weighted alpha
   * Formula: smoothed = alpha * current + (1 - alpha) * previous
   * Alpha is calculated based on visibility (confidence score)
   */
  private smoothLandmark(current: PoseLandmark, previous: PoseLandmark): PoseLandmark {
    // Calculate alpha based on visibility
    // Higher visibility (>0.7) = alpha ~0.5-0.7 (more responsive)
    // Lower visibility (<0.7) = alpha ~0.2-0.4 (more stable)
    const alpha = this.calculateAlpha(current.visibility);

    return {
      x: alpha * current.x + (1 - alpha) * previous.x,
      y: alpha * current.y + (1 - alpha) * previous.y,
      z: alpha * current.z + (1 - alpha) * previous.z,
      visibility: current.visibility // Don't smooth visibility itself
    };
  }

  /**
   * Calculate alpha value based on landmark visibility (confidence score)
   * Higher visibility = higher alpha = more responsive
   * Lower visibility = lower alpha = more stable
   */
  private calculateAlpha(visibility: number): number {
    if (visibility >= 0.7) {
      // High confidence: map 0.7-1.0 to alpha 0.5-0.7
      return 0.5 + (visibility - 0.7) / 0.3 * 0.2;
    } else {
      // Low confidence: map 0.0-0.7 to alpha 0.2-0.5
      return 0.2 + (visibility / 0.7) * 0.3;
    }
  }

  /**
   * Clone landmarks structure for storing previous state
   */
  private cloneLandmarks(landmarks: PoseData['landmarks']): PoseData['landmarks'] {
    return {
      nose: { ...landmarks.nose },
      leftEye: { ...landmarks.leftEye },
      rightEye: { ...landmarks.rightEye },
      leftEar: { ...landmarks.leftEar },
      rightEar: { ...landmarks.rightEar },
      leftShoulder: { ...landmarks.leftShoulder },
      rightShoulder: { ...landmarks.rightShoulder },
      leftElbow: { ...landmarks.leftElbow },
      rightElbow: { ...landmarks.rightElbow },
      leftWrist: { ...landmarks.leftWrist },
      rightWrist: { ...landmarks.rightWrist },
      leftHip: { ...landmarks.leftHip },
      rightHip: { ...landmarks.rightHip },
      leftKnee: { ...landmarks.leftKnee },
      rightKnee: { ...landmarks.rightKnee },
      leftAnkle: { ...landmarks.leftAnkle },
      rightAnkle: { ...landmarks.rightAnkle },
    };
  }

  /**
   * Reset smoother state (clear previous smoothed values)
   */
  reset(): void {
    this.previousSmoothed = null;
  }
}
